import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FAQ_INSIGHT_CATEGORY_SEARCH_API_URL, FAQ_INSIGHT_SMART_SEARCH_API_URL } from '../../utils/apiUrls';
import { debounce } from '../../utils/debounce';
import { getAccessToken } from '../../utils/token-service';
import useClickOutside from '../../utils/use-click-outside';

interface KnowledgeBaseSearchProps {
	handleSendClick: (inputValue: string) => void;
	stepId: any;
}
interface ApiResponse {
	id: number;
	faq: string;
	answer: string;
	rank: number;
}

const KnowledgeBaseSearch: React.FC<KnowledgeBaseSearchProps> = ({ handleSendClick, stepId }) => {
	const [stepFaqs, setStepFaqs] = useState<ApiResponse[]>([]);
	const inputRef = useRef<HTMLInputElement>(null);
	const suggestionBoxRef = useRef<HTMLDivElement>(null);
	const [suggestions, setSuggestions] = useState<{ faq: string }[]>([]);
	const [loading, setLoading] = useState<boolean>(false);
	const [errorMessage, setErrorMessage] = useState<string>('');

	useEffect(() => {
		const fetchData = async () => {
			try {
				const accessToken = await getAccessToken();
				const response = await fetch(`${FAQ_INSIGHT_CATEGORY_SEARCH_API_URL}/${stepId}`, {
					method: 'GET',
					headers: {
						Authorization: `Bearer ${accessToken}`,
					},
				});
				if (!response.ok) {
					throw new Error(`HTTP error! Status: ${response.status}`);
				}
				const responseData = await response.json();
				setStepFaqs(responseData);
			} catch (error) {
				console.log('Error calling API:', error);
			}
		};

		fetchData();
	}, [stepId]);

	const fetchSmartSearchData = useCallback(
		async (searchQuery: string) => {
			try {
				const accessToken = await getAccessToken();
				const data = JSON.stringify({
					faq_category_id: stepId,
					query_string: searchQuery,
					limit: 5,
				});
				const response = await fetch(FAQ_INSIGHT_SMART_SEARCH_API_URL, {
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
						Authorization: `Bearer ${accessToken}`,
					},
					body: data,
				});
				if (!response.ok) {
					throw new Error(`HTTP error! Status: ${response.status}`);
				}
				const responseData = await response.json();
				setSuggestions(responseData);
			} catch (error) {
				console.log('Error calling API:', error);
			}
		},
		[stepId, getAccessToken]
	);

	const fetchSmartSearchDataDebounced = useCallback(
		debounce((searchQuery: string) => {
			fetchSmartSearchData(searchQuery);
		}, 300),
		[fetchSmartSearchData]
	);

	const handleInputChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			const searchQuery = event.target.value.trim();
			if (searchQuery.length < 3) {
				setSuggestions(stepFaqs.slice(0, 5));
			} else if (searchQuery.length >= 3) {
				fetchSmartSearchDataDebounced(searchQuery);
			}
		},
		[fetchSmartSearchData]
	);

	// const handleInputFocus = () => {
	// 	setSuggestions(stepFaqs.slice(0, 5));
	// };

	const handleSuggestionClick = useCallback((suggestion: string) => {
		if (inputRef.current) {
			inputRef.current.value = suggestion;
			setSuggestions([]);
		}
	}, []);

	const handleClickOutside = () => {
		setSuggestions([]);
	};
	useClickOutside(suggestionBoxRef, handleClickOutside);

	const handleSendButtonClick = async () => {
		setLoading(true);
		const inputValue = inputRef.current?.value.trim();
		if (!inputValue) {
			setErrorMessage('Input cannot be empty');
			setLoading(false);
			return;
		}
		setErrorMessage('');
		try {
			await handleSendClick(inputValue);
		} catch (error) {
			console.log('Error sending request:', error);
		} finally {
			setLoading(false);
		}
	};

	useClickOutside(suggestionBoxRef, handleClickOutside);

	return (
		<>
			<div className="flex rounded-lg bg-[#CDF6FF]">
				<div className="relative flex h-10 rounded-lg flex-grow items-stretch focus-within:z-10">
					<input
						type="text"
						ref={inputRef}
						className="block h-10 w-full outline-none bg-[#CDF6FF] focus:bg-white rounded-lg border-0 py-1.5 pl-3 pr-10 text-[#1F1F1F] ring-0 focus:border-none focus:ring-0 focus-visible:ring-0 placeholder:text-[#1F1F1F] text-sm font-normal"
						placeholder="Ask a question"
						// onFocus={handleInputFocus}
						onChange={handleInputChange}
					/>
					<button
						type="button"
						title="send"
						onClick={handleSendButtonClick}
						className="absolute right-0 pr-2 h-10 top-0 w-10 flex items-center justify-center"
					>
						{loading ? '...' : <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="size-6">
							<path fillRule="evenodd" d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25Zm.53 5.47a.75.75 0 0 0-1.06 0l-3 3a.75.75 0 1 0 1.06 1.06l1.72-1.72v5.69a.75.75 0 0 0 1.5 0v-5.69l1.72 1.72a.75.75 0 1 0 1.06-1.06l-3-3Z" clipRule="evenodd" />
						</svg>
						}
					</button>
					{suggestions.length > 0 && (
						<div
							ref={suggestionBoxRef}
							className="absolute z-10 w-full bg-white shadow-sm rounded-b-lg px-2 pt-1 top-9 overflow-hidden"
						>
							<div className="pb-3 overflow-y-auto max-h-36">
								<div className="mb-2 h-[1px] bg-[#f1f1f1]" />
								{suggestions.map((suggestion, index) => (
									<div
										key={index}
										className="px-1 py-1 cursor-pointer text-xs leading-5 font-semibold"
										onClick={() => handleSuggestionClick(suggestion.faq)}
									>
										{suggestion.faq}
									</div>
								))}
							</div>
						</div>
					)}
				</div>
			</div>
			{errorMessage && (
				<p className="text-red-700 font-medium text-[12px] my-1 flex gap-1 items-center">
					{errorMessage}
				</p>
			)}
		</>
	);
};

export default KnowledgeBaseSearch;
